import React, {useCallback, useMemo} from 'react';
import {
    useCopyBenchMutation,
    useDeleteBenchMutation,
} from 'components/customer/BTM/store/btmApi';
import {useAppContext} from 'contexts/ApplicationContext';
import {useDialogContext} from 'contexts/DialogContext';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useNavigate} from 'react-router-dom';
import {formatPrice, genericMessageHandler} from 'shared/helpers';
import {useGetBenchId} from 'components/customer/BTM/helper/useGetBenchId';
import {useAppDispatch} from 'store/customer';
import {clearAll} from 'components/customer/BTM/store/btmSlice';
import {
    useFetchBenchesData,
    BenchTableInterface as Bench,
} from 'components/customer/BTM/helper/useFetchBenchesData';
import {BenchtopDetail} from 'components/customer/JobDashboard/BenchtopDetail';
import {PartialJob} from 'shared/types/PartialJob';
import {ProductType} from 'components/customer/JobDashboard/helpers/useProductQuantity';
import {isMobile} from 'react-device-detect';
import {ProductQuantity} from 'components/customer/JobDashboard/columns/ProductQuantity';

export enum View {
    JOB_DASHBOARD,
    CART,
}

export const useBenchtopTableConfig = (view = View.JOB_DASHBOARD) => {
    const {benchId} = useGetBenchId();
    const dispatch = useAppDispatch();
    const {notify} = useNotificationContext();
    const navigate = useNavigate();
    const {userProfile} = useAppContext();
    const {
        room,
        job,
        isPageDirty,
        actionButtonsEnabled,
        isJobProcessing,
        updateTotalProductCountManual,
    } = useJobContext() as PartialJob;

    const {showDialog} = useDialogContext();

    const {benches, isLoaded} = useFetchBenchesData();
    const [deleteBench] = useDeleteBenchMutation();
    const [copyBench] = useCopyBenchMutation();

    const addBenchtopUrl = `/v2/job/${job.displayId}/benchtop-module`;

    const removeBenchtop = useCallback(
        (bench: Bench, event: React.MouseEvent<HTMLElement>) => {
            const button = (event.target as HTMLElement)
                .parentElement as HTMLButtonElement;
            showDialog({
                title: 'Delete benchtop',
                message:
                    'Are you sure you want to delete this benchtop from the job?',
                yes: async () => {
                    try {
                        if (bench.isOld) {
                            return;
                        }

                        button.disabled = true;

                        await deleteBench({benchId: bench.id});
                        updateTotalProductCountManual(true, false);

                        button.disabled = false;
                        genericMessageHandler(
                            notify,
                            {message: 'Benchtop removed from the job'},
                            'success'
                        );

                        if (benchId == bench.id) {
                            void navigate(`/v2/job/${job.displayId}/dashboard`);
                        }
                    } catch (e) {
                        button.disabled = false;
                        genericMessageHandler(notify, e);
                    }
                },
            });
        },
        [deleteBench, updateTotalProductCountManual, job, room]
    );

    const editBenchtop = useCallback(
        (row: Bench) => {
            if (!row.isOld) {
                dispatch(clearAll());
                void navigate(
                    `/v2/job/${job.displayId}/benchtop-module/type/${row.shape.type}/materialType/${row.type.id}/material?bench=${row.id}`
                );
            }
        },
        [job]
    );

    const strongFormatter = useCallback(
        (value: string, row: Bench) => {
            if (row.id == Number(benchId)) {
                return <strong>{value}</strong>;
            } else {
                return value;
            }
        },
        [benchId]
    );

    const redirectoToAddBenchtop = useCallback(() => {
        void navigate(addBenchtopUrl);
    }, [addBenchtopUrl]);

    const noRoomAndVariationBenchtop = useCallback(
        (variations: number) => {
            if (variations > 0) {
                return <>No Benchtops.</>;
            } else {
                return (
                    <>
                        No Benchtops.{' '}
                        {job.displayId && isJobProcessing ? (
                            <span
                                style={{color: 'red', cursor: 'pointer'}}
                                onClick={redirectoToAddBenchtop}>
                                <strong>Click Add BenchTops</strong> to add one.
                            </span>
                        ) : null}
                    </>
                );
            }
        },
        [redirectoToAddBenchtop]
    );

    const copyBTM = useCallback(
        (bench: Bench, event: React.MouseEvent) => {
            event.stopPropagation();
            const button = (event.target as HTMLElement)
                .parentElement as HTMLButtonElement;
            showDialog({
                title: 'Copy benchtop',
                message:
                    'Are you sure you want to copy this benchtop from the job?',
                yes: async () => {
                    try {
                        button.disabled = true;
                        await copyBench({id: bench.id});
                        updateTotalProductCountManual(
                            true,
                            true,
                            bench.quantity
                        );

                        button.disabled = false;
                        genericMessageHandler(
                            notify,
                            {message: 'Benchtop copied'},
                            'success'
                        );
                    } catch (e) {
                        button.disabled = false;
                        genericMessageHandler(notify, e);
                    }
                },
            });
        },
        [copyBench, showDialog]
    );

    const tableConfig = useMemo(() => {
        let tableConfig;

        if (view == View.CART) {
            tableConfig = {
                fields: [
                    {
                        fieldName: 'jobBenchtopNumber',
                        title: '#',
                        format: strongFormatter,
                    },
                    {
                        fieldName: 'name',
                        title: 'Current Benchtops',
                        format: (value: string, row: Bench) => {
                            if (row.quantity > 1) {
                                return strongFormatter(
                                    `${row.quantity} x ${value}`,
                                    row
                                );
                            } else {
                                return strongFormatter(value, row);
                            }
                        },
                    },
                    {
                        fieldName: 'dimension',
                        title: 'Dimensions',
                        width: 80,
                        format: strongFormatter,
                    },
                    userProfile.show_pricing && {
                        fieldName: 'cost',
                        title: 'Cost',
                        width: 65,
                        format: (value: string, row: Bench) =>
                            strongFormatter(
                                formatPrice(parseFloat(value), {}),
                                row
                            ),
                    },
                ],
                data: benches,
                noRecordsText: (
                    <>{noRoomAndVariationBenchtop(job.variationsConfirmed)}</>
                ),
                options: {},
            };
        } else {
            tableConfig = {
                fields: [
                    {
                        fieldName: 'jobBenchtopNumber',
                        title: '#',
                        width: 35,
                        format: (value: string) => <strong>{value}</strong>,
                    },
                    {
                        fieldName: 'name',
                        title: 'Benchtop',
                        format: (field, row: Bench) => (
                            <BenchtopDetail bench={row} />
                        ),
                    },
                    {
                        fieldName: 'dimension',
                        title: 'Dimensions (mm)',
                        mobile: false,
                        width: 170,
                    },
                    userProfile.show_pricing && {
                        fieldName: 'cost',
                        mobile: false,
                        title: (
                            <div>
                                Unit Price (ex.{' '}
                                {job.countryTaxRateName
                                    ? job.countryTaxRateName
                                    : 'Tax'}
                                )
                            </div>
                        ),
                        width: 150,
                        format: (value: string, row: Bench) =>
                            formatPrice(parseFloat(value) / row.quantity, {}),
                    },
                    {
                        fieldName: 'quantity',
                        mobile: false,
                        title: 'Quantity',
                        width: 190,
                        minWidth: 85,
                        format: function PriceField(field: string, row: Bench) {
                            if (!actionButtonsEnabled || !isJobProcessing) {
                                return field;
                            }
                            return (
                                <ProductQuantity
                                    value={Number(field)}
                                    productDetails={row}
                                    type={ProductType.BTM}
                                />
                            );
                        },
                    },
                    userProfile.show_pricing && {
                        fieldName: 'cost',
                        title: (
                            <div>
                                TOTAL (ex.{' '}
                                {job.countryTaxRateName
                                    ? job.countryTaxRateName
                                    : 'Tax'}
                                )
                            </div>
                        ),
                        mobile: false,
                        width: 150,
                        format: (value: string) => (
                            <div className="total">
                                {formatPrice(parseFloat(value), {})}
                            </div>
                        ),
                    },
                ].filter((field) => field),
                data:
                    benches && isLoaded
                        ? benches.filter((bench) => !bench.isOld)
                        : undefined,
                noRecordsText: (
                    <>{noRoomAndVariationBenchtop(job.variationsConfirmed)}</>
                ),
                options: {},
            };
        }

        if (isJobProcessing && actionButtonsEnabled) {
            tableConfig.options = {
                title: '',
                width: view == View.JOB_DASHBOARD ? (isMobile ? 50 : 150) : 75,
                alignment: 'center',
                buttons: [
                    {
                        iconName: 'Options-Edit.svg',
                        title: 'Edit Benchtop',
                        action: editBenchtop,
                        disabled: (row: Bench) => row.id == benchId,
                        isHidden: (row: Bench) => row.isOld,
                    },
                    {
                        iconName: 'Options-Copy.svg',
                        title: 'Copy Product',
                        action: (row: Bench, event: React.MouseEvent) =>
                            copyBTM(row, event),
                        isHidden: (row: Bench) => row.isOld,
                    },
                    {
                        iconName: 'Options-Delete.svg',
                        title: 'Delete Benchtop',
                        action: removeBenchtop,
                        confirmChanges: isPageDirty,
                        isHidden: (row: Bench) => row.isOld,
                    },
                ],
            };
        }

        if (!actionButtonsEnabled) {
            tableConfig.options = undefined;
        }

        return tableConfig;
    }, [
        benchId,
        job,
        room,
        benches,
        isPageDirty,
        actionButtonsEnabled,
        isJobProcessing,
        navigate,
        view,
        copyBTM,
    ]);

    return {
        tableConfig,
    };
};
